import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import type { Crumb } from "../utils/structuredData";
import SlotPicker from "../components/SlotPicker";
import BookingConfirmation from "../components/BookingConfirmation";
import {
  type Availability,
  type Booking,
  formatMeeting,
  newIdempotencyKey,
  SchedulerError,
  schedulerApi,
} from "../services/schedulerApi";

const crumbs: Crumb[] = [
  { name: "Home", path: "/" },
  { name: "Manage Your Meeting", path: "/book/manage" },
];

type Mode = "view" | "reschedule" | "confirm-cancel";

// Linked from the calendar invite and the booking confirmation: move or cancel a booked meeting.
const BookManage: React.FC = () => {
  const [params] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("view");
  const [changed, setChanged] = useState<"rescheduled" | "cancelled" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const actionKey = useRef<{ action: string; key: string } | null>(null);

  const keyFor = (action: string) => {
    if (actionKey.current?.action !== action) actionKey.current = { action, key: newIdempotencyKey() };
    return actionKey.current.key;
  };
  const fail = (error: unknown) =>
    setNotice(error instanceof SchedulerError ? error.message : "Something went wrong. Please try again.");

  useEffect(() => {
    const value = params.get("token");
    setToken(value);
    if (!value) {
      setNotice("This link is incomplete. Please use the link from your calendar invite.");
      setBusy(false);
      return;
    }
    schedulerApi.manage(value).then((r) => setBooking(r.booking)).catch(fail).finally(() => setBusy(false));
  }, [params]);

  const startReschedule = async () => {
    if (!token) return;
    setBusy(true);
    setNotice(null);
    try {
      setAvailability(await schedulerApi.availability({ token }));
      setSelected(null);
      setMode("reschedule");
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const confirmReschedule = async () => {
    if (!token || !selected || !availability) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await schedulerApi.reschedule(token, selected, keyFor(`reschedule:${selected}`));
      setBooking(result.booking);
      setChanged("rescheduled");
      setMode("view");
    } catch (error) {
      if (error instanceof SchedulerError && error.code === "SLOT_TAKEN" && error.slots) {
        setAvailability({ ...availability, slots: error.slots });
        setSelected(null);
      }
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    if (!token) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await schedulerApi.cancel(token, keyFor("cancel"));
      setBooking(result.booking);
      setChanged("cancelled");
      setMode("view");
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const secondary =
    "inline-flex items-center justify-center rounded-full border border-borderSubtle bg-white px-6 py-3 font-semibold text-text-primary hover:bg-surfaceSoft disabled:opacity-60";
  const primary =
    "inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 font-semibold text-text-inverse hover:bg-primaryHover disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <div className="relative pt-6 pb-16">
      <Seo title="Manage Your Meeting | Contextral" description="Reschedule or cancel your Contextral meeting." noindex />
      <section>
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="font-bold text-text-primary mb-4 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Manage Your Meeting
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            Reschedule or cancel your meeting with Contextral.
          </p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto">
        <div className="rounded-2xl border border-borderSubtle bg-surface shadow-xl p-6 sm:p-8 md:p-10">
          {notice && <p role="alert" className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{notice}</p>}
          {busy && !booking && <p className="text-text-secondary">Loading your meeting...</p>}

          {booking && mode === "view" && (booking.cancelled && booking.cancelled_by_lead ? (
            <div className="text-center py-6">
              <h2 className="text-2xl font-bold text-text-primary mb-2">Please pick a new time</h2>
              <p className="text-text-secondary mb-6">
                {booking.lead.name} had to cancel your meeting on {formatMeeting(booking.starts_at, booking.timezone)}.
                Choose a new time that works for you.
              </p>
              <button type="button" className={primary} disabled={busy} onClick={() => void startReschedule()}>
                Pick a new time
              </button>
            </div>
          ) : booking.cancelled ? (
            <div className="text-center py-6">
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                {changed === "cancelled" ? "Your meeting is cancelled" : "This meeting was cancelled"}
              </h2>
              <p className="text-text-secondary mb-6">
                {formatMeeting(booking.starts_at, booking.timezone)} with {booking.lead.name} is off the calendar.
              </p>
              {booking.meeting_type === "assessment" && (
                <Link to="/book" className={primary}>Book a new time</Link>
              )}
            </div>
          ) : (
            <>
              <BookingConfirmation
                booking={booking}
                heading={changed === "rescheduled" ? "Your meeting has moved" : "Your upcoming meeting"}
                showManageLink={false}
              />
              {booking.can_change ? (
                <div className="mt-2 flex flex-col sm:flex-row gap-3 justify-center">
                  <button type="button" className={secondary} disabled={busy} onClick={() => void startReschedule()}>
                    Pick a new time
                  </button>
                  <button type="button" className={secondary} disabled={busy} onClick={() => setMode("confirm-cancel")}>
                    Cancel meeting
                  </button>
                </div>
              ) : (
                <p className="text-center text-text-secondary">
                  This meeting can no longer be changed online. Reply to your calendar invite if anything has come up.
                </p>
              )}
            </>
          ))}

          {booking && mode === "confirm-cancel" && (
            <div className="text-center py-6">
              <h2 className="text-2xl font-bold text-text-primary mb-2">Cancel this meeting?</h2>
              <p className="text-text-secondary mb-6">
                {formatMeeting(booking.starts_at, booking.timezone)} with {booking.lead.name}. Everyone on the invite
                will be notified.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button type="button" className={secondary} disabled={busy} onClick={() => setMode("view")}>
                  Keep meeting
                </button>
                <button type="button" className={primary} disabled={busy} onClick={() => void confirmCancel()}>
                  {busy ? "Cancelling..." : "Yes, cancel it"}
                </button>
              </div>
            </div>
          )}

          {booking && mode === "reschedule" && availability && (
            <div>
              <p className="text-text-secondary mb-5">
                {booking.cancelled
                  ? "Choose a new time:"
                  : `Currently ${formatMeeting(booking.starts_at, booking.timezone)}. Choose a new time:`}
              </p>
              <SlotPicker slots={availability.slots} timeZone={availability.timezone} selected={selected}
                onSelect={setSelected} disabled={busy} />
              <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
                <button type="button" className={secondary} disabled={busy} onClick={() => setMode("view")}>Back</button>
                <button type="button" className={primary} disabled={busy || !selected}
                  onClick={() => void confirmReschedule()}>
                  {busy ? "Saving..." : booking.cancelled ? "Book this time" : "Move meeting"}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookManage;
